import type { FC } from 'react'

import { config } from '@skytech/config'
import { Checkbox, Input, LoaderCircle, Select, Skeleton } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import { resultStatuses } from '@/modules/admin/resources/assets/constants'
import type { LeasingAssetDetailsReturn } from '@/resources/leasing'
import { leasingResources } from '@/resources/leasing'

import type { FormFields } from '../types'

import { getSelectOptions } from '../../helpers'

const isDevEnv = config.ENVIRONMENT === 'dev'

export const LeasingInfo: FC = () => {
  const params = useParams()
  const assetId = Number(params.id)
  const translate = useTranslation()

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))

  // TODO: enable for production
  const { data, isLoading, isError } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    {
      enabled: isDevEnv,
    }
  )
  const {
    data: assetTypes,
    isLoading: isAssetTypesLoading,
    isError: isAssetTypesError,
  } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  // TODO: enable for production
  if ((isDevEnv && isError) || isAssetTypesError) {
    return <LoadingError />
  }
  if ((isDevEnv && isLoading) || isAssetTypesLoading) {
    return <LoaderCircle />
  }

  const leasingInfo = data?.leasingInfo
  const availableEndingOptions = leasingInfo?.endOfLeaseAvailableEndingOptions?.split(',') || []
  const leasingFieldLocks = data?.leasingFieldLocks
  const newLockedFieldsList = data?.lockFields

  return (
    <div className="form-column-set">
      <div className="form-column">
        <Field name="returnStatusId">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="returnStatusId"
              label={<Translation id="Return status" />}
              options={
                getSelectOptions(
                  assetTypes?.returnStatuses.map(({ id, description }) => ({
                    id,
                    label: description,
                  })),
                  translate
                ) || []
              }
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>

        <div className="two-fields flex flex-wrap gap-1">
          <Field name="leaseStart">
            {({ field, meta }: FieldProps) => (
              <Input
                capitalizeLabel
                isFullWidth
                isDisabled={
                  leasingFieldLocks?.leaseStart || newLockedFieldsList?.includes('leaseStart')
                }
                id="leaseStart"
                type="date"
                ariaLabel={translate('Lease start')}
                label={<Translation id="Lease start" />}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>

          <Field name="leaseEnd">
            {({ field, meta, form }: FieldProps<FormFields['leaseStart'], FormFields>) => (
              <Input
                capitalizeLabel
                isFullWidth
                isDisabled={
                  leasingFieldLocks?.leaseEnd || newLockedFieldsList?.includes('leaseEnd')
                }
                id="leaseEnd"
                type="date"
                ariaLabel={translate('Lease end')}
                label={<Translation id="Lease end" />}
                marginBottom={16}
                min={form.values.leaseStart || ''}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
        </div>

        <Field name="result">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="result"
              label={<Translation id="Result" />}
              options={
                resultStatuses.map(status => ({
                  value: status,
                  label: status,
                })) || []
              }
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>

        <Field name="resultCost">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="resultCost"
              type="number"
              ariaLabel={translate('Result cost')}
              label={<Translation id="Result cost" />}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
              value={field.value === null ? '' : field.value}
            />
          )}
        </Field>
      </div>

      <div className="form-column">
        <Field name="deleteCertificateDate">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="deleteCertificateDate"
              type="date"
              ariaLabel={translate('Delete certificate date')}
              label={<Translation id="Delete certificate date" />}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
            />
          )}
        </Field>

        <Field name="deleteCertificate">
          {({ field }: FieldProps) => (
            <Checkbox
              capitalizeLabel
              id="deleteCertificate"
              label={<Translation id="Delete certificate" />}
              marginBottom={32}
              checked={field.value}
              {...field}
            />
          )}
        </Field>

        {isDevEnv && !isError && (
          <LeasingInfoSection
            isLoading={isLoading}
            leasingInfo={leasingInfo}
            availableEndingOptions={availableEndingOptions}
          />
        )}
      </div>
    </div>
  )
}

const LeasingInfoSection: FC<{
  isLoading: boolean
  leasingInfo: LeasingAssetDetailsReturn['leasingInfo']
  availableEndingOptions: string[]
}> = ({ isLoading, leasingInfo, availableEndingOptions }) => {
  if (isLoading) {
    return <Skeleton width={200} />
  }
  return (
    <>
      {leasingInfo && (
        <section>
          <DataPoints>
            {leasingInfo.leasePeriod && (
              <DataPoint
                header={<Translation id="Lease period" />}
                value={String(leasingInfo.leasePeriod)}
              />
            )}

            {(leasingInfo.endOfLeaseProcessStartDate ||
              leasingInfo.endOfLeaseEmployeeDeadlineDate) && (
              <>
                <h3>
                  <Translation id="End of lease" />
                </h3>
                {leasingInfo.endOfLeaseProcessStartDate && (
                  <DataPoint
                    header={<Translation id="Process start date" />}
                    value={<LocalDateTime date={leasingInfo.endOfLeaseProcessStartDate} />}
                  />
                )}
                {leasingInfo.endOfLeaseEmployeeDeadlineDate && (
                  <DataPoint
                    header={<Translation id="Employee deadline date" />}
                    value={<LocalDate date={leasingInfo.endOfLeaseEmployeeDeadlineDate} />}
                  />
                )}
                <DataPoint />
              </>
            )}
            {(leasingInfo.endOfLeaseCurrentEndingOption || availableEndingOptions.length > 0) && (
              <>
                <h3>
                  <Translation id="Ending options" />
                </h3>
                {leasingInfo.endOfLeaseCurrentEndingOption && (
                  <DataPoint
                    header={<Translation id="Current" />}
                    value={<Translation id={leasingInfo.endOfLeaseCurrentEndingOption} />}
                  />
                )}
                {availableEndingOptions.length > 0 && (
                  <DataPoint
                    header={<Translation id="Available" />}
                    values={availableEndingOptions.map(el => (
                      <Translation id={el.trim()} key={el} />
                    ))}
                  />
                )}
              </>
            )}
            {leasingInfo.terminationDate && (
              <DataPoint
                header={<Translation id="Termination date" />}
                value={<LocalDateTime date={leasingInfo.terminationDate} />}
              />
            )}
            {leasingInfo.customerBuyoutPrice && (
              <DataPoint
                header={<Translation id="Customer buyout price" />}
                value={<FormattedNumberWithCurrency amount={leasingInfo.customerBuyoutPrice} />}
              />
            )}
          </DataPoints>
        </section>
      )}
    </>
  )
}
